import { useParams } from 'react-router-dom';
import { cn } from '@/lib/cn';
import { ChatSidebar } from './chat-sidebar';
import { ChatThread } from './chat-thread';

/**
 * Mobile-first chat layout.
 *
 *   Mobile (<md)
 *     - No thread selected: full-viewport list (sidebar).
 *     - Thread selected: the thread component is pinned fixed to the
 *       viewport so it covers the app header and gives the user a
 *       phone-app feel. The sidebar is hidden.
 *
 *   Desktop (md+)
 *     - Classic two-pane split (sidebar + thread) beneath the header.
 *       The fixed styling we apply on mobile collapses back to a simple
 *       flex container.
 */
export function ChatPage() {
  const { threadId } = useParams<{ threadId: string }>();

  return (
    <div
      className={cn(
        'flex w-full bg-gray-50',
        threadId
          ? // Thread open → on mobile, become a full-viewport overlay that
            // sits above the sticky header. On desktop, fall back to the
            // header-below container height.
            'fixed inset-0 z-30 h-[100dvh] md:static md:z-auto md:h-[calc(100vh-64px)]'
          : 'h-[calc(100dvh-64px)]',
      )}
    >
      {/* Sidebar: full-width on mobile list view, constrained on desktop.
          Hidden on mobile when a thread is selected — the thread covers the
          whole screen and users navigate back via the thread's back button. */}
      <div
        className={cn(
          'flex w-full flex-col border-r border-gray-200 md:w-80 md:max-w-sm md:shrink-0',
          threadId ? 'hidden md:flex' : 'flex',
        )}
      >
        <ChatSidebar selectedId={threadId} />
      </div>

      {/* Right pane / thread area */}
      {threadId ? (
        <ChatThread transactionId={threadId} />
      ) : (
        <div className="hidden flex-1 items-center justify-center bg-gray-50 md:flex">
          <div className="max-w-md text-center">
            <h2 className="text-base font-semibold text-gray-900">Select a conversation</h2>
            <p className="mt-1 text-sm text-gray-600">
              Your transactions appear on the left. Pick one to see the conversation.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
